import React from "react";

import { Stack, StackProps, useTheme } from "@mui/material";

import { IgButton } from "./IgButton";

export type IgSegmentedControlOption<T extends string> = {
  disabled?: boolean;
  label: React.ReactNode;
  value: T;
};

export type IgSegmentedControlProps<T extends string> = Omit<
  StackProps,
  "children" | "onChange"
> & {
  allowDeselect?: boolean;
  ariaLabel: string;
  disabled?: boolean;
  onChange: (value: T | null) => void;
  options: IgSegmentedControlOption<T>[];
  value: T | null;
};

const SEGMENTED_CONTROL_BUTTON_SX = {
  border: "1px solid transparent",
  borderRadius: "6px",
  flex: 1,
  justifyContent: "center",
  "&:hover": {
    borderColor: "transparent",
  },
};

export const IgSegmentedControl = <T extends string>({
  allowDeselect = true,
  ariaLabel,
  disabled = false,
  onChange,
  options,
  sx,
  value,
  ...rest
}: IgSegmentedControlProps<T>) => {
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      role="group"
      aria-label={ariaLabel}
      spacing={0.25}
      sx={[
        {
          border: `1px solid ${theme.igBorders.core.default}`,
          borderRadius: "8px",
          p: "2px",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      {options.map((option) => {
        const isSelected = value === option.value;

        return (
          <IgButton
            key={option.value}
            variant={isSelected ? "contained" : "outlined"}
            size="mini"
            color={isSelected ? "primary" : "secondary"}
            aria-pressed={isSelected}
            disabled={disabled || option.disabled}
            onClick={() => {
              if (allowDeselect && isSelected) {
                onChange(null);
                return;
              }

              onChange(option.value);
            }}
            sx={SEGMENTED_CONTROL_BUTTON_SX}
          >
            {option.label}
          </IgButton>
        );
      })}
    </Stack>
  );
};
